import { useState, useEffect, useCallback, useMemo } from "react";
import DropDownList from "../../components/Atomos/DropDown/Dropdown";
import CalendarInput from "../../components/Atomos/Inputs/CalendarInput";
import { useEventForm } from "../../hooks/Events/useEventForm";
import TimeInput from "../../components/Atomos/Inputs/TimeInput";
import SwitchToggle from "../../components/Atomos/Switch-Toogle/Switch";
import { useEventValidation } from "../../hooks/Events/useEventValidation";
import {
  timestampToTimeString,
  timestampToDateString,
} from "../../utils/dates";
import { timezonesData, ITimezone } from "../../utils/timezones";
import {
  buildFlexibleEventMonthOptions,
  buildFlexibleEventYearOptions,
  formatFlexibleEventMonthYear,
  parseFlexibleEventMonthYear,
} from "../../utils/eventFlexibleDate";

interface DatesProps {
  validation?: ReturnType<typeof useEventValidation>;
}

const normalizeTimestampToMinutes = (timestamp: number): number => {
  const date = new Date(timestamp * 1000);
  date.setUTCSeconds(0, 0);
  return Math.floor(date.getTime() / 1000);
};

const combineDateAndTime = (
  dateString: string,
  timeString: string
): number | null => {
  if (!dateString) {
    return null;
  }

  const [month, day, year] = dateString.split("/");
  if (!month || !day || !year) {
    return null;
  }

  const timeParts = timeString.split(":");
  const hours = parseInt(timeParts[0] || "0", 10);
  const minutes = parseInt(timeParts[1] || "0", 10);
  const seconds = 0;

  const date = new Date(
    Date.UTC(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
      hours,
      minutes,
      seconds
    )
  );
  return Math.floor(date.getTime() / 1000);
};

const parseDateString = (dateStr: string): Date | null => {
  const [month, day, year] = dateStr.split("/");
  if (!month || !day || !year) {
    return null;
  }
  return new Date(
    parseInt(year, 10),
    parseInt(month, 10) - 1,
    parseInt(day, 10)
  );
};

const parseTimeString = (timeStr: string): number => {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

const formatUTCOffset = (timediff: number): string => {
  const totalMinutes = Math.abs(timediff);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const sign = timediff >= 0 ? "+" : "-";

  const hoursStr = hours.toString().padStart(2, "0");
  const minutesStr = minutes.toString().padStart(2, "0");

  return `UTC${sign}${hoursStr}:${minutes === 0 ? "00" : minutesStr}`;
};

const formatTimezoneName = (tz: ITimezone): string => {
  const city = tz.timecity || "";
  const country = tz.country || "";
  const state = tz.timestate || "";
  const utcOffset = formatUTCOffset(tz.timediff);

  let displayName = "";
  if (country && city) {
    displayName = state
      ? `${country} - ${state} - ${city}`
      : `${country} - ${city}`;
  } else if (country) {
    displayName = country;
  } else if (city) {
    displayName = city;
  } else {
    displayName = tz.iana_timezone;
  }

  return `${displayName} ${utcOffset} (${tz.iana_timezone})`;
};

const Dates = ({ validation }: DatesProps) => {
  const { event, updateEventField } = useEventForm();
  const localValidation = useEventValidation();
  const validationHook = validation || localValidation;
  const [startDateString, setStartDateString] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [endDateString, setEndDateString] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [endDateError, setEndDateError] = useState<string>("");

  const timezoneData = useMemo(() => {
    return timezonesData
      .map((tz) => ({
        id: tz.iana_timezone,
        name: formatTimezoneName(tz),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const endDateMinDate = useMemo(() => {
    if (!startDateString) {
      return undefined;
    }
    return parseDateString(startDateString);
  }, [startDateString]);

  const selectedTimezoneName = useMemo(() => {
    if (!event?.timeZone) {
      return "";
    }

    const timezone = timezonesData.find(
      (tz) => tz.iana_timezone === event.timeZone
    );
    return timezone ? formatTimezoneName(timezone) : event.timeZone;
  }, [event?.timeZone]);

  const validateEndDateTime = useCallback(
    (
      endDateStr: string,
      endTimeStr: string,
      startDateStr: string,
      startTimeStr: string
    ): string => {
      if (!endDateStr) {
        return "";
      }
      if (!startDateStr) {
        return "Please select a start date first";
      }

      const startDate = parseDateString(startDateStr);
      const endDate = parseDateString(endDateStr);

      if (!startDate || !endDate) {
        return "";
      }

      if (endDate < startDate) {
        return "End date must be on or after start date";
      }

      if (
        endDate.getTime() === startDate.getTime() &&
        endTimeStr &&
        startTimeStr
      ) {
        const startTotalMinutes = parseTimeString(startTimeStr);
        const endTotalMinutes = parseTimeString(endTimeStr);

        if (endTotalMinutes <= startTotalMinutes) {
          return "End time must be after start time when dates are the same";
        }
      }

      return "";
    },
    []
  );

  useEffect(() => {
    if (!event?.startDate) {
      setStartTime("");
      setStartDateString("");
      return;
    }

    const normalizedTimestamp = normalizeTimestampToMinutes(event.startDate);
    const timeStr = timestampToTimeString(normalizedTimestamp);
    const dateStr = timestampToDateString(normalizedTimestamp);

    setStartTime(timeStr);
    setStartDateString(dateStr);

    if (normalizedTimestamp !== event.startDate) {
      updateEventField("startDate", normalizedTimestamp);
    }
  }, [event?.startDate, updateEventField]);

  const updateFieldWithValidation = useCallback(
    (
      fieldName: "startDate" | "endDate" | "timeZone",
      value: number | string | undefined
    ) => {
      updateEventField(fieldName, value);
      if (validationHook.hasAttemptedSave) {
        validationHook.validateField(fieldName, value, event);
      }
    },
    [updateEventField, validationHook, event]
  );

  const updateDateTime = useCallback(
    (dateStr: string, timeStr: string, fieldName: "startDate" | "endDate") => {
      if (dateStr) {
        const timestamp = combineDateAndTime(dateStr, timeStr || "00:00");
        if (timestamp !== null) {
          const normalizedTimestamp = normalizeTimestampToMinutes(timestamp);
          updateFieldWithValidation(fieldName, normalizedTimestamp);
        }
      } else {
        updateFieldWithValidation(fieldName, undefined);
      }
    },
    [updateFieldWithValidation]
  );

  const handleStartDateChange = useCallback(
    (dateStr: string) => {
      setStartDateString(dateStr);
      updateDateTime(dateStr, startTime, "startDate");

      if (endDateString) {
        const error = validateEndDateTime(
          endDateString,
          endTime,
          dateStr,
          startTime
        );
        setEndDateError(error);
      }
    },
    [startTime, updateDateTime, endDateString, endTime, validateEndDateTime]
  );

  const handleStartTimeChange = useCallback(
    (timeStr: string) => {
      setStartTime(timeStr);
      updateDateTime(startDateString, timeStr, "startDate");

      if (endDateString) {
        const error = validateEndDateTime(
          endDateString,
          endTime,
          startDateString,
          timeStr
        );
        setEndDateError(error);
      }
    },
    [
      startDateString,
      updateDateTime,
      endDateString,
      endTime,
      validateEndDateTime,
    ]
  );

  const handleEndDateChange = useCallback(
    (dateStr: string) => {
      setEndDateString(dateStr);

      if (!dateStr) {
        updateFieldWithValidation("endDate", undefined);
        setEndDateError("");
        return;
      }

      const error = validateEndDateTime(
        dateStr,
        endTime || "00:00",
        startDateString,
        startTime
      );
      setEndDateError(error);

      if (!error) {
        updateDateTime(dateStr, endTime, "endDate");
      }
    },
    [
      endTime,
      startDateString,
      startTime,
      updateDateTime,
      updateFieldWithValidation,
      validateEndDateTime,
    ]
  );

  const handleEndTimeChange = useCallback(
    (timeStr: string) => {
      setEndTime(timeStr);

      if (!endDateString) {
        if (!timeStr) {
          updateFieldWithValidation("endDate", undefined);
          setEndDateError("");
        }
        return;
      }

      const error = validateEndDateTime(
        endDateString,
        timeStr || "00:00",
        startDateString,
        startTime
      );
      setEndDateError(error);

      if (!error) {
        updateDateTime(endDateString, timeStr, "endDate");
      }
    },
    [
      endDateString,
      startDateString,
      startTime,
      updateDateTime,
      updateFieldWithValidation,
      validateEndDateTime,
    ]
  );

  const handleClearStartDate = useCallback(() => {
    setStartDateString("");
    setStartTime("");
    updateFieldWithValidation("startDate", undefined);

    if (endDateString) {
      const error = validateEndDateTime(endDateString, endTime, "", startTime);
      setEndDateError(error);
    }
  }, [
    updateFieldWithValidation,
    endDateString,
    endTime,
    startTime,
    validateEndDateTime,
  ]);

  const handleClearStartTime = useCallback(() => {
    setStartTime("");
    if (startDateString) {
      updateDateTime(startDateString, "", "startDate");
      if (endDateString) {
        const error = validateEndDateTime(
          endDateString,
          endTime,
          startDateString,
          ""
        );
        setEndDateError(error);
      }
    } else {
      updateFieldWithValidation("startDate", undefined);
    }
  }, [
    startDateString,
    updateDateTime,
    updateFieldWithValidation,
    endDateString,
    endTime,
    validateEndDateTime,
  ]);

  const handleClearEndDate = useCallback(() => {
    setEndTime("");
    setEndDateString("");
    setEndDateError("");
    updateFieldWithValidation("endDate", undefined);
  }, [updateFieldWithValidation]);

  const handleClearEndTime = useCallback(() => {
    setEndTime("");
    if (endDateString) {
      const timestamp = combineDateAndTime(endDateString, "00:00");
      const error = validateEndDateTime(
        endDateString,
        "00:00",
        startDateString,
        startTime
      );
      setEndDateError(error);
      if (!error && timestamp !== null) {
        const normalizedTimestamp = normalizeTimestampToMinutes(timestamp);
        updateFieldWithValidation("endDate", normalizedTimestamp);
      }
    } else {
      updateFieldWithValidation("endDate", undefined);
      setEndDateError("");
    }
  }, [
    endDateString,
    startDateString,
    startTime,
    updateFieldWithValidation,
    validateEndDateTime,
  ]);

  const handleTimezoneChange = useCallback(
    (opt: { id: string; name: string } | null) => {
      const ianaTimezone = opt?.id || undefined;
      updateFieldWithValidation("timeZone", ianaTimezone);
    },
    [updateFieldWithValidation]
  );

  const handleComingSoonToggle = useCallback(() => {
    updateEventField("comingSoonDate", !event?.comingSoonDate);
  }, [updateEventField, event?.comingSoonDate]);

  const [flexibleMonth, setFlexibleMonth] = useState<number | null>(null);
  const [flexibleYear, setFlexibleYear] = useState<number | null>(null);

  useEffect(() => {
    const parsed = parseFlexibleEventMonthYear(event?.comingSoonDateMessage);
    if (!parsed) {
      return;
    }
    setFlexibleMonth(parsed.month);
    setFlexibleYear(parsed.year);
  }, [event?.comingSoonDateMessage]);

  const monthOptions = useMemo(() => buildFlexibleEventMonthOptions(), []);
  const yearOptions = useMemo(() => buildFlexibleEventYearOptions(), []);

  const selectedFlexibleMonthName = useMemo(() => {
    if (!flexibleMonth) {
      return "";
    }
    return monthOptions.find((option) => Number(option.id) === flexibleMonth)?.name ?? "";
  }, [flexibleMonth, monthOptions]);

  const selectedFlexibleYearName = useMemo(
    () => (flexibleYear ? String(flexibleYear) : ""),
    [flexibleYear],
  );

  const applyFlexibleMonthYear = useCallback(
    (month: number | null, year: number | null) => {
      if (!month || !year) {
        updateEventField("comingSoonDateMessage", "");
        return;
      }
      updateEventField(
        "comingSoonDateMessage",
        formatFlexibleEventMonthYear(month, year),
      );
    },
    [updateEventField],
  );

  const handleFlexibleMonthChange = useCallback(
    (opt: { id: string; name: string } | null) => {
      const month = Number(opt?.id) || null;
      setFlexibleMonth(month);
      applyFlexibleMonthYear(month, flexibleYear);
    },
    [applyFlexibleMonthYear, flexibleYear],
  );

  const handleFlexibleYearChange = useCallback(
    (opt: { id: string; name: string } | null) => {
      const year = Number(opt?.id) || null;
      setFlexibleYear(year);
      applyFlexibleMonthYear(flexibleMonth, year);
    },
    [applyFlexibleMonthYear, flexibleMonth],
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      if (!validationHook.hasAttemptedSave) {
        return undefined;
      }
      return validationHook.errors[fieldName];
    },
    [validationHook.hasAttemptedSave, validationHook.errors]
  );

  const hasFieldError = useCallback(
    (fieldName: string) => {
      return (
        validationHook.hasAttemptedSave && !!validationHook.errors[fieldName]
      );
    },
    [validationHook.hasAttemptedSave, validationHook.errors]
  );

  useEffect(() => {
    if (!event?.endDate) {
      setEndTime("");
      setEndDateString("");
      setEndDateError("");
      return;
    }

    const normalizedTimestamp = normalizeTimestampToMinutes(event.endDate);
    const timeStr = timestampToTimeString(normalizedTimestamp);
    const dateStr = timestampToDateString(normalizedTimestamp);

    setEndTime(timeStr);
    setEndDateString(dateStr);

    if (startDateString) {
      const error = validateEndDateTime(
        dateStr,
        timeStr,
        startDateString,
        startTime
      );
      setEndDateError(error);
    } else {
      setEndDateError("");
    }

    if (normalizedTimestamp !== event.endDate) {
      updateEventField("endDate", normalizedTimestamp);
    }
  }, [
    event?.endDate,
    updateEventField,
    startDateString,
    startTime,
    validateEndDateTime,
  ]);

  const isComingSoon = !!event?.comingSoonDate;
  const isRequired = !isComingSoon;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2 md:gap-[.625rem]">
        <SwitchToggle
          checked={isComingSoon}
          seter={handleComingSoonToggle}
          titleFirst
          title="Coming Soon / Month & Year Only"
        />
        {isComingSoon && (
          <>
            <h3 className="text-sm text-gray-600 font-Roboto leading-5">
              If no month or date is specified, the event will automatically
              display &apos;Coming Soon&apos; instead of a date.
            </h3>
            <div className="grid grid-cols-12 gap-4 md:gap-6 lg:gap-8">
              <div className="col-span-12 md:col-span-6 lg:col-span-3">
                <DropDownList
                  title="Month"
                  selectedObjst={selectedFlexibleMonthName}
                  seter={handleFlexibleMonthChange}
                  error={false}
                  errormsj=""
                  data={monthOptions}
                  getid
                />
              </div>
              <div className="col-span-12 md:col-span-6 lg:col-span-3">
                <DropDownList
                  title="Year"
                  selectedObjst={selectedFlexibleYearName}
                  seter={handleFlexibleYearChange}
                  error={false}
                  errormsj=""
                  data={yearOptions}
                  getid
                />
              </div>
            </div>
          </>
        )}
      </div>

      {!isComingSoon && (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-12 gap-4 md:gap-6 lg:gap-8">
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <CalendarInput
                title="Start Date"
                type="single"
                value={startDateString}
                onChange={handleStartDateChange}
                onClear={handleClearStartDate}
                required={isRequired}
                error={hasFieldError("startDate")}
                errorMsj={getFieldError("startDate")}
              />
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <TimeInput
                title="Start Time"
                value={startTime}
                placeholder="HH:MM AM/PM"
                required={isRequired}
                error={hasFieldError("startDate")}
                errorMsj={getFieldError("startDate")}
                onChange={handleStartTimeChange}
                onClear={handleClearStartTime}
                minuteInterval={15}
              />
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <CalendarInput
                title="End Date"
                type="single"
                value={endDateString}
                onChange={handleEndDateChange}
                onClear={handleClearEndDate}
                required={false}
                error={!!endDateError}
                errorMsj={endDateError}
                minDate={endDateMinDate || undefined}
              />
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <TimeInput
                title="End Time"
                value={endTime}
                placeholder="HH:MM AM/PM"
                required={false}
                error={!!endDateError}
                errorMsj={endDateError}
                onChange={handleEndTimeChange}
                onClear={handleClearEndTime}
                minuteInterval={15}
              />
            </div>
          </div>
          <div className="w-full lg:w-1/2">
            <DropDownList
              title="Timezone"
              required={false}
              selectedObjst={selectedTimezoneName}
              seter={handleTimezoneChange}
              error={false}
              errormsj={""}
              data={timezoneData}
              getid
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Dates;
